<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { ChevronDown, ChevronUp, Loader2, Search, FileText, Layers } from 'lucide-vue-next'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'

const props = defineProps({
  sessionUuid: {
    type: String,
    required: true
  },
  organizationId: {
    type: Number,
    required: true
  }
})

const store = useStore()

const isExpanded = ref(false)
const isLoading = ref(false)
const error = ref(null)
const errorDialog = ref(null)
const errorMessage = ref('')
const projectSearchQuery = ref('')
const layerSearchQuery = ref('')
const togglingIds = ref({})

const prompts = computed(() => {
  return store.getters['chat/getSessionPrompts'](props.sessionUuid)
})

const promptsCount = computed(() => {
  return store.getters['chat/getTotalPromptsCount'](props.sessionUuid)
})

// All org projects
const allProjects = computed(() => {
  const projects = store.getters['project/getProjects'] || []
  return projects.filter(p => p.organization_id === props.organizationId)
})

// All org layers
const allLayers = computed(() => {
  const layers = store.getters['layers/getOrgLayers'](props.organizationId) || []
  return layers.filter(l => l.properties.type === 4)
})

// Connected entity_id sets for O(1) lookup
const connectedProjectIds = computed(() => {
  const ids = new Set()
  if (prompts.value?.projects) {
    for (const p of prompts.value.projects) {
      ids.add(p.entity_id)
    }
  }
  return ids
})

const connectedLayerIds = computed(() => {
  const ids = new Set()
  if (prompts.value?.layers) {
    for (const p of prompts.value.layers) {
      ids.add(p.entity_id)
    }
  }
  return ids
})

// Filtered and sorted lists (connected items first)
const filteredProjects = computed(() => {
  let list = allProjects.value
  if (projectSearchQuery.value) {
    const q = projectSearchQuery.value.toLowerCase()
    list = list.filter(p =>
      p.project_name.toLowerCase().includes(q) ||
      (p.project_desc && p.project_desc.toLowerCase().includes(q))
    )
  }
  return [...list].sort((a, b) => {
    const aConnected = connectedProjectIds.value.has(a.project_id) ? 0 : 1
    const bConnected = connectedProjectIds.value.has(b.project_id) ? 0 : 1
    return aConnected - bConnected
  })
})

const filteredLayers = computed(() => {
  let list = allLayers.value
  if (layerSearchQuery.value) {
    const q = layerSearchQuery.value.toLowerCase()
    list = list.filter(l => l.properties.name.toLowerCase().includes(q))
  }
  return [...list].sort((a, b) => {
    const aConnected = connectedLayerIds.value.has(a.id) ? 0 : 1
    const bConnected = connectedLayerIds.value.has(b.id) ? 0 : 1
    return aConnected - bConnected
  })
})

const allProjectsSelected = computed(() => {
  if (allProjects.value.length === 0) return false
  return allProjects.value.every(p => connectedProjectIds.value.has(p.project_id))
})

const allLayersSelected = computed(() => {
  if (allLayers.value.length === 0) return false
  return allLayers.value.every(l => connectedLayerIds.value.has(l.id))
})

// Find the prompt object for a connected entity
const findProjectPrompt = (projectId) => {
  return prompts.value?.projects?.find(p => p.entity_id === projectId)
}

const findLayerPrompt = (layerId) => {
  return prompts.value?.layers?.find(p => p.entity_id === layerId)
}

const isProjectDisabled = (projectId) => {
  if (togglingIds.value[`project-${projectId}`]) return true
  const prompt = findProjectPrompt(projectId)
  return prompt && prompt.is_removable === false
}

const isLayerDisabled = (layerId) => {
  if (togglingIds.value[`layer-${layerId}`]) return true
  const prompt = findLayerPrompt(layerId)
  return prompt && prompt.is_removable === false
}

const fetchPrompts = async () => {
  isLoading.value = true
  error.value = null

  try {
    await store.dispatch('chat/fetchSessionPrompts', {
      orgId: props.organizationId,
      sessionUuid: props.sessionUuid
    })
  } catch (err) {
    console.error('Error fetching prompts:', err)
    error.value = err.message || 'Failed to load prompts'
  } finally {
    isLoading.value = false
  }
}

const toggleProject = async (project) => {
  const key = `project-${project.project_id}`
  if (togglingIds.value[key]) return

  togglingIds.value = { ...togglingIds.value, [key]: true }
  try {
    if (connectedProjectIds.value.has(project.project_id)) {
      const prompt = findProjectPrompt(project.project_id)
      if (prompt && prompt.is_removable !== false) {
        await store.dispatch('chat/unlinkPromptAction', {
          orgId: props.organizationId,
          sessionUuid: props.sessionUuid,
          promptId: prompt.id,
          entityType: 'project'
        })
      }
    } else {
      await store.dispatch('chat/linkProjectPromptAction', {
        orgId: props.organizationId,
        sessionUuid: props.sessionUuid,
        projectId: project.project_id
      })
    }
  } catch (err) {
    console.error('Error toggling project:', err)
    errorMessage.value = 'Failed to toggle project: ' + (err.message || 'Unknown error')
    errorDialog.value.open()
  } finally {
    const { [key]: _, ...rest } = togglingIds.value
    togglingIds.value = rest
  }
}

const toggleLayer = async (layer) => {
  const key = `layer-${layer.id}`
  if (togglingIds.value[key]) return

  togglingIds.value = { ...togglingIds.value, [key]: true }
  try {
    if (connectedLayerIds.value.has(layer.id)) {
      const prompt = findLayerPrompt(layer.id)
      if (prompt && prompt.is_removable !== false) {
        await store.dispatch('chat/unlinkPromptAction', {
          orgId: props.organizationId,
          sessionUuid: props.sessionUuid,
          promptId: prompt.id,
          entityType: 'layer'
        })
      }
    } else {
      await store.dispatch('chat/linkLayerPromptAction', {
        orgId: props.organizationId,
        sessionUuid: props.sessionUuid,
        layerId: layer.id
      })
    }
  } catch (err) {
    console.error('Error toggling layer:', err)
    errorMessage.value = 'Failed to toggle layer: ' + (err.message || 'Unknown error')
    errorDialog.value.open()
  } finally {
    const { [key]: _, ...rest } = togglingIds.value
    togglingIds.value = rest
  }
}

const toggleAllProjects = async () => {
  const shouldLink = !allProjectsSelected.value
  for (const project of allProjects.value) {
    const isConnected = connectedProjectIds.value.has(project.project_id)
    if (shouldLink && !isConnected) {
      await toggleProject(project)
    } else if (!shouldLink && isConnected) {
      const prompt = findProjectPrompt(project.project_id)
      if (prompt && prompt.is_removable !== false) {
        await toggleProject(project)
      }
    }
  }
}

const toggleAllLayers = async () => {
  const shouldLink = !allLayersSelected.value
  for (const layer of allLayers.value) {
    const isConnected = connectedLayerIds.value.has(layer.id)
    if (shouldLink && !isConnected) {
      await toggleLayer(layer)
    } else if (!shouldLink && isConnected) {
      const prompt = findLayerPrompt(layer.id)
      if (prompt && prompt.is_removable !== false) {
        await toggleLayer(layer)
      }
    }
  }
}

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value
  if (isExpanded.value && !prompts.value) {
    fetchPrompts()
  }
}

// Fetch prompts on mount
onMounted(() => {
  store.dispatch('layers/fetchOrgLayers', { orgId: props.organizationId })
  if (props.sessionUuid) {
    fetchPrompts()
  }
})

// Watch for session changes
watch(() => props.sessionUuid, (newUuid) => {
  if (newUuid) {
    store.dispatch('layers/fetchOrgLayers', { orgId: props.organizationId })
    fetchPrompts()
  }
})
</script>

<template>
  <div class="border-t border-border bg-background">
    <!-- Header -->
    <button
      @click="toggleExpand"
      class="w-full px-3 py-2 flex items-center justify-between hover:bg-muted/80 transition-colors"
    >
      <div class="flex items-center gap-2">
        <span class="text-sm font-medium text-foreground">Connections</span>
        <span
          v-if="promptsCount > 0"
          class="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full"
        >
          {{ promptsCount }}
        </span>
      </div>
      <ChevronUp v-if="isExpanded" class="h-4 w-4 text-muted-foreground" />
      <ChevronDown v-else class="h-4 w-4 text-muted-foreground" />
    </button>

    <!-- Expanded content -->
    <div
      v-if="isExpanded"
      class="px-3 pb-3 space-y-3 max-h-96 overflow-y-auto"
    >
      <!-- Loading state -->
      <div v-if="isLoading" class="flex items-center justify-center py-8">
        <Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
        <span class="ml-2 text-sm text-muted-foreground">Loading prompts...</span>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="text-center py-8">
        <p class="text-sm text-red-600">{{ error }}</p>
        <Button variant="outline" size="sm" @click="fetchPrompts" class="mt-3">
          Retry
        </Button>
      </div>

      <!-- Content -->
      <template v-else-if="prompts">
        <!-- PROJECTS section -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Projects ({{ connectedProjectIds.size }}/{{ allProjects.length }})
            </h3>
            <div v-if="allProjects.length > 0" class="flex items-center gap-2">
              <span class="text-xs text-muted-foreground">All</span>
              <Switch
                :model-value="allProjectsSelected"
                @update:model-value="toggleAllProjects"
                :disabled="allProjects.length === 0"
                class="scale-75"
              />
            </div>
          </div>

          <!-- Search (shown if > 3 items) -->
          <div v-if="allProjects.length > 3" class="relative mb-2">
            <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              v-model="projectSearchQuery"
              placeholder="Search projects..."
              class="h-8 pl-8 text-xs"
            />
          </div>

          <!-- Empty state -->
          <div v-if="allProjects.length === 0" class="text-sm text-muted-foreground text-center py-4 bg-background rounded border border-dashed border-border">
            No projects available
          </div>

          <!-- No search results -->
          <div v-else-if="filteredProjects.length === 0" class="text-xs text-muted-foreground text-center py-3">
            No projects match "{{ projectSearchQuery }}"
          </div>

          <!-- Project list -->
          <TransitionGroup v-else name="list" tag="div" class="space-y-1 relative">
            <div
              v-for="project in filteredProjects"
              :key="project.project_id"
              class="flex items-center justify-between py-1.5 px-2 rounded hover:bg-background/60 transition-all duration-300"
            >
              <div class="flex items-center gap-2 min-w-0 flex-1">
                <FileText class="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                <span class="text-sm truncate" :class="connectedProjectIds.has(project.project_id) ? 'text-foreground' : 'text-muted-foreground'">
                  {{ project.project_name }}
                </span>
              </div>
              <Switch
                :model-value="connectedProjectIds.has(project.project_id)"
                @update:model-value="toggleProject(project)"
                :disabled="isProjectDisabled(project.project_id)"
                class="flex-shrink-0 scale-75"
              />
            </div>
          </TransitionGroup>
        </div>

        <!-- LAYERS section -->
        <div>
          <div class="flex items-center justify-between mb-2 mt-4">
            <h3 class="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Layers ({{ connectedLayerIds.size }}/{{ allLayers.length }})
            </h3>
            <div v-if="allLayers.length > 0" class="flex items-center gap-2">
              <span class="text-xs text-muted-foreground">All</span>
              <Switch
                :model-value="allLayersSelected"
                @update:model-value="toggleAllLayers"
                :disabled="allLayers.length === 0"
                class="scale-75"
              />
            </div>
          </div>

          <!-- Search (shown if > 3 items) -->
          <div v-if="allLayers.length > 3" class="relative mb-2">
            <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              v-model="layerSearchQuery"
              placeholder="Search layers..."
              class="h-8 pl-8 text-xs"
            />
          </div>

          <!-- Empty state -->
          <div v-if="allLayers.length === 0" class="text-sm text-muted-foreground text-center py-4 bg-background rounded border border-dashed border-border">
            No layers available
          </div>

          <!-- No search results -->
          <div v-else-if="filteredLayers.length === 0" class="text-xs text-muted-foreground text-center py-3">
            No layers match "{{ layerSearchQuery }}"
          </div>

          <!-- Layer list -->
          <TransitionGroup v-else name="list" tag="div" class="space-y-1 relative">
            <div
              v-for="layer in filteredLayers"
              :key="layer.uuid"
              class="flex items-center justify-between py-1.5 px-2 rounded hover:bg-background/60 transition-all duration-300"
            >
              <div class="flex items-center gap-2 min-w-0 flex-1">
                <Layers class="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                <span class="text-sm truncate" :class="connectedLayerIds.has(layer.id) ? 'text-foreground' : 'text-muted-foreground'">
                  {{ layer.properties.name }}
                </span>
              </div>
              <Switch
                :model-value="connectedLayerIds.has(layer.id)"
                @update:model-value="toggleLayer(layer)"
                :disabled="isLayerDisabled(layer.id)"
                class="flex-shrink-0 scale-75"
              />
            </div>
          </TransitionGroup>
        </div>
      </template>
    </div>

    <!-- Error Dialog -->
    <DialogConfirm
      ref="errorDialog"
      title="Error"
      :description="errorMessage"
      confirm-text="OK"
      :show-cancel="false"
      variant="error"
    />
  </div>
</template>

<style scoped>
.list-move {
  transition: transform 0.3s ease;
}
.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}
.list-leave-active {
  position: absolute;
}
</style>
